import { useCallback, useMemo } from 'react';
import { t, Trans } from '@lingui/macro';
import { useFormContext } from 'react-hook-form';
import { CountryCode } from 'libphonenumber-js';

import {
  TextField,
  Checkbox,
  Combobox,
  TextArea,
  PhoneTextField,
} from 'components/Form';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { COUNTRY_OPTIONS } from 'pages/Management/constants';
import { getStatesByCountry } from 'utils/countryRegions';

import {
  Asterisk,
  AsteriskSmall,
  ContentWrapper,
  PhoneNumberWrapper,
  AddressRowWrapper,
  RequiredMessage,
  SectionLabel,
  DepotsWrapper,
} from '../EditContactModal/styles';

import { FormValues, codeToCountry } from './schema';

interface ContactFormProps {
  depotsSection?: React.ReactNode;
}

const ContactForm = ({ depotsSection }: ContactFormProps) => {
  const { watch, setValue } = useFormContext();

  const countryCode: FormValues['mobilePhoneCountryCode'] = watch(
    'mobilePhoneCountryCode',
  );

  const country: FormValues['country'] = watch('country');

  const stateOptions = useMemo(
    () => (country ? getStatesByCountry(country) : []),
    [country],
  );

  const handleCountryChange = useCallback(() => {
    setValue('state', '');
  }, [setValue]);

  return (
    <ContentWrapper>
      <RequiredMessage>
        <Asterisk />
        <P shade={1} size="small">{t`Required Fields`}</P>
      </RequiredMessage>
      <TextField name="firstName" label={t`First Name`} required />
      <TextField name="lastName" label={t`Last Name`} required />
      <Flex>
        <TextArea
          name="notes"
          label={t`Notes`}
          maxLength={200}
          helpMessage={t`Max 200 characters`}
        />
      </Flex>
      <Flex flexDirection="column">
        <SectionLabel>
          <P>
            <Trans>Notifications</Trans>
          </P>
          <AsteriskSmall />
        </SectionLabel>
        <P size="small" shade={1}>
          <Trans>Enter an email address or a mobile phone number.</Trans>
        </P>
      </Flex>
      <TextField name="email" label={t`Email`} />
      <Checkbox
        name="emailNotificationEnabled"
        label={t`Send Notification by Email`}
        size="small"
      />
      <PhoneNumberWrapper>
        <Combobox
          name="mobilePhoneCountryCode"
          label={t`Country Code`}
          options={[
            { id: '+1', label: '+1', value: '+1' },
            { id: '+52', label: '+52', value: '+52' },
            { id: '+56', label: '+56', value: '+56' },
            { id: '+593', label: '+593', value: '+593' },
            { id: '+81', label: '+81', value: '+81' },
          ]}
          defaultValue="+1"
          variant="sync"
        />
        <PhoneTextField
          name="mobilePhone"
          label={t`Mobile Phone Number`}
          countryCode={codeToCountry(countryCode || '+1') as CountryCode}
        />
      </PhoneNumberWrapper>
      <Checkbox
        name="smsNotificationEnabled"
        label={t`Send Notification by Text Message`}
        size="small"
      />
      <SectionLabel>
        <P>
          <Trans>Address</Trans>
        </P>
      </SectionLabel>
      <TextField name="address1" label={t`Address`} />
      <TextField name="address2" label={t`Address 2`} />
      <AddressRowWrapper>
        <Combobox
          name="country"
          label={t`Country`}
          options={COUNTRY_OPTIONS}
          onChange={handleCountryChange}
          defaultValue=""
          variant="sync"
        />
        <Combobox
          name="state"
          label={t`State`}
          options={stateOptions}
          clearButtonLabel={t`Clear State`}
          defaultValue=""
          variant="sync"
        />
      </AddressRowWrapper>
      <AddressRowWrapper>
        <TextField name="city" label={t`City`} />
        <TextField name="postalCode" label={t`Postal Code`} />
      </AddressRowWrapper>
      {depotsSection && <DepotsWrapper>{depotsSection}</DepotsWrapper>}
    </ContentWrapper>
  );
};

export default ContactForm;
